import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { ExternalLink, Calendar, CalendarCheck, Building2, ClipboardList, Package, FolderKanban, FileText, Pencil } from 'lucide-react';
import { TimelineEvent, TimelineEventType, getEventTypeLabel } from '../../types/timeline';
import TimelineAttachments from './TimelineAttachments';
import CheckLocationSection from '../../Pages/BuildingChecks/components/CheckLocationSection';
import { sumAllocation } from '../../Helpers/budgetAllocation';
import { getDatabase, ref, get } from 'firebase/database';
import { ProjectModel, ProjectStatus } from '../../types/projects';
import { BaseComponent } from '../../types/protocols';
import { translate } from '../../types/EnumTranslations';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { getMaterialTranslation } from '../../contexts/MaterialsContext';
import { parseStorageDate } from '../../Helpers/DateUtils';

interface TimelineEventDetailModalProps {
    show: boolean;
    onHide: () => void;
    event: TimelineEvent | null;
    /** When provided and the event is editable (custom), shows a "Redigera" action. */
    onEdit?: (event: TimelineEvent) => void;
    /** UID → display name; undefined for an unknown user. */
    userName?: (uid: string) => string | undefined;
}

/**
 * Modal for displaying timeline event details
 * Handles all event types with appropriate metadata display
 */
const TimelineEventDetailModal: React.FC<TimelineEventDetailModalProps> = ({
    show,
    onHide,
    event,
    onEdit,
    userName
}) => {
    const { getTranslation } = useBuildComponentTypes();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetched data for different event types
    const [project, setProject] = useState<ProjectModel | null>(null);
    const [component, setComponent] = useState<BaseComponent | null>(null);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setProject(null);
            setComponent(null);
            setError(null);
        }
    }, [show]);

    // Fetch data based on event type
    useEffect(() => {
        if (!show || !event) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            const db = getDatabase();

            try {
                switch (event.eventType) {
                    case TimelineEventType.PROJECT_END:
                        if (event.projectId) {
                            const snapshot = await get(ref(db, `projects/${event.projectId}`));
                            if (snapshot.exists()) {
                                setProject({ ...snapshot.val(), key: event.projectId });
                            }
                        }
                        break;

                    case TimelineEventType.COMPONENT_REPLACED:
                        if (event.componentKey && event.buildingKey) {
                            const snapshot = await get(ref(db, `buildings/${event.buildingKey}/structure/components/${event.componentKey}`));
                            if (snapshot.exists()) {
                                setComponent(snapshot.val());
                            }
                        }
                        break;

                    case TimelineEventType.INSPECTION_COMPLETED:
                        // Inspection data is already in the event
                        break;

                    case TimelineEventType.CUSTOM:
                        // Custom event data is already in the event
                        break;
                }
            } catch (err) {
                console.error('Error fetching event data:', err);
                setError('Kunde inte ladda händelsedata');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [show, event]);

    if (!event) return null;

    // Format date for display
    const formatDate = (dateStr: string | undefined): string => {
        if (!dateStr) return '-';
        try {
            const date = parseStorageDate(dateStr);
            return date.toLocaleDateString('sv-SE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    // Date plus the stored Stockholm time (hh:mm), read from the string so the
    // viewer's time zone cannot shift it
    const formatDateTime = (dateStr: string): string => {
        const time = dateStr.match(/\s(\d{2}:\d{2})/)?.[1];
        return time ? `${formatDate(dateStr)} kl. ${time}` : formatDate(dateStr);
    };

    // Get icon for event type
    const getIcon = () => {
        switch (event.eventType) {
            case TimelineEventType.INSPECTION_COMPLETED:
                return <ClipboardList size={20} className="text-warning" />;
            case TimelineEventType.COMPONENT_REPLACED:
                return <Package size={20} className="text-purple" />;
            case TimelineEventType.PROJECT_END:
                return <FolderKanban size={20} className="text-primary" />;
            case TimelineEventType.MAINTENANCE_PLAN_ACTIVATED:
                return <CalendarCheck size={20} className="text-success" />;
            case TimelineEventType.CUSTOM:
                return <FileText size={20} className="text-info" />;
            default:
                return <Calendar size={20} />;
        }
    };

    // Render the building name, linking to the building page (new tab) when a key is available
    const renderBuildingName = () => {
        if (event.buildingKey) {
            return (
                <a
                    href={`/building/${event.buildingKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {event.buildingName}
                </a>
            );
        }
        return <span>{event.buildingName}</span>;
    };

    // Render content based on event type
    const renderContent = () => {
        if (loading) {
            return (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                    <p className="mt-2 text-muted">Laddar...</p>
                </div>
            );
        }

        if (error) {
            return <div className="text-danger">{error}</div>;
        }

        switch (event.eventType) {
            case TimelineEventType.PROJECT_END:
                return renderProjectContent();
            case TimelineEventType.COMPONENT_REPLACED:
                return renderComponentContent();
            case TimelineEventType.INSPECTION_COMPLETED:
                return renderInspectionContent();
            case TimelineEventType.MAINTENANCE_PLAN_ACTIVATED:
                return renderMaintenancePlanContent();
            case TimelineEventType.CUSTOM:
                return renderCustomContent();
            default:
                return <div>Okänd händelsetyp</div>;
        }
    };

    const renderProjectContent = () => {
        const proj = project;
        if (!proj) return <div className="text-muted">Kunde inte ladda projekt</div>;

        const getStatusBadge = (status: ProjectStatus) => {
            switch (status) {
                case ProjectStatus.PLANNED:
                    return <Badge bg="secondary">Planerad</Badge>;
                case ProjectStatus.IN_PROGRESS:
                    return <Badge bg="primary">Pågående</Badge>;
                case ProjectStatus.COMPLETED:
                    return <Badge bg="success">Utförd</Badge>;
                default:
                    return null;
            }
        };

        return (
            <>
                <div className="mb-3">
                    <h5>{proj.title}</h5>
                </div>

                <Row className="mb-3">
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Building2 size={14} className="text-muted" />
                            <strong>Byggnad</strong>
                        </div>
                        {renderBuildingName()}
                    </Col>
                    <Col xs={6}>
                        <strong>Status</strong>
                        <div>{getStatusBadge(proj.status)}</div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col xs={6}>
                        <strong>Startdatum</strong>
                        <div>{formatDate(proj.startDate)}</div>
                    </Col>
                    <Col xs={6}>
                        <strong>Slutdatum</strong>
                        <div>{formatDate(proj.actualEndDate || proj.plannedEndDate)}</div>
                    </Col>
                </Row>

                {proj.budget && (
                    <div className="mb-3 p-2 bg-light rounded">
                        <strong>Budget:</strong> {sumAllocation(proj.budget).toLocaleString('sv-SE')} kr
                    </div>
                )}
            </>
        );
    };

    const renderComponentContent = () => {
        const comp = component;
        if (!comp) return <div className="text-muted">Kunde inte ladda komponent</div>;

        const baseType = comp.type ? translate(comp.type) : null;
        const specificType = comp.specificType?.value ? getTranslation(comp.specificType.value, comp.type) : null;
        const label = comp.label?.value;
        const warrantyStartDate = comp.warrantyStartDate?.value;
        const material = comp.material?.value?.length
            ? comp.material.value.map(m => getMaterialTranslation(m)).join(', ')
            : null;

        return (
            <>
                <div className="mb-3">
                    <h5>{label || specificType || baseType || 'Komponent'}</h5>
                </div>

                <Row className="mb-3">
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Building2 size={14} className="text-muted" />
                            <strong>Byggnad</strong>
                        </div>
                        {renderBuildingName()}
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Calendar size={14} className="text-muted" />
                            <strong>Garantistart</strong>
                        </div>
                        <span>{warrantyStartDate || '-'}</span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    {baseType && (
                        <Col xs={6}>
                            <strong>Komponenttyp</strong>
                            <div>{baseType}</div>
                        </Col>
                    )}
                    {specificType && specificType !== baseType && (
                        <Col xs={6}>
                            <strong>Specifik typ</strong>
                            <div>{specificType}</div>
                        </Col>
                    )}
                </Row>

                {material && (
                    <Row className="mb-3">
                        <Col xs={12}>
                            <strong>Material</strong>
                            <div>{material}</div>
                        </Col>
                    </Row>
                )}

                {comp.condition?.value && (
                    <div className="mb-3 p-2 bg-light rounded">
                        <strong>Skick:</strong> {comp.condition.value.value}/10
                        {comp.condition.value.comment && (
                            <span className="text-muted ms-2">({comp.condition.value.comment})</span>
                        )}
                    </div>
                )}
            </>
        );
    };

    const renderInspectionContent = () => {
        return (
            <>
                <div className="mb-3">
                    <h5>{event.inspectionTemplateName || 'Kontroll'}</h5>
                </div>

                <Row className="mb-3">
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Building2 size={14} className="text-muted" />
                            <strong>Byggnad</strong>
                        </div>
                        {renderBuildingName()}
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Calendar size={14} className="text-muted" />
                            <strong>Utförd</strong>
                        </div>
                        <span>{formatDate(event.displayDate)}</span>
                    </Col>
                </Row>

                {event.inspectionMeasurement && (
                    <div className="mb-3 p-2 bg-light rounded">
                        <strong>Mätvärde:</strong> {event.inspectionMeasurement.value} {event.inspectionMeasurement.unit}
                        <Badge
                            bg={event.inspectionMeasurement.withinRange ? 'success' : 'danger'}
                            className="ms-2"
                        >
                            {event.inspectionMeasurement.withinRange ? 'Inom gräns' : 'Utanför gräns'}
                        </Badge>
                    </div>
                )}

                <CheckLocationSection
                    buildingKey={event.buildingKey}
                    check={{ componentKey: event.inspectionComponentKey, location: event.inspectionLocation }}
                />
            </>
        );
    };

    const renderMaintenancePlanContent = () => {
        const plan = event.maintenancePlan;
        if (!plan) return null;
        const activatedByName = (plan.activatedBy && userName?.(plan.activatedBy)) || 'okänd användare';

        return (
            <>
                <div className="mb-3">
                    <h5>Underhållsplan</h5>
                    {plan.isCurrent && <Badge bg="success">Aktiv plan</Badge>}
                </div>

                <Row className="mb-3">
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Building2 size={14} className="text-muted" />
                            <strong>Byggnad</strong>
                        </div>
                        {renderBuildingName()}
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Calendar size={14} className="text-muted" />
                            <strong>Aktiverad</strong>
                        </div>
                        <span>{formatDateTime(plan.activatedAt)} av {activatedByName}</span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col xs={6}>
                        <strong>Komponenter</strong>
                        <div>{plan.componentCount}</div>
                    </Col>
                    <Col xs={6}>
                        <strong>Underhållsaktiviteter</strong>
                        <div>{plan.activityCount}</div>
                    </Col>
                </Row>

                {plan.activationCount > 1 && (
                    <div className="mb-2 text-muted">
                        Aktiverad {plan.activationCount} gånger sedan {formatDate(plan.firstActivatedAt)}
                    </div>
                )}
                <div className="mb-3 text-muted">
                    {plan.previousActivatedAt
                        ? `Föregående plan aktiverad ${formatDate(plan.previousActivatedAt)}`
                        : 'Första underhållsplanen för byggnaden'}
                </div>
            </>
        );
    };

    const renderCustomContent = () => {
        return (
            <>
                <div className="mb-3">
                    {event.title && event.title.trim() && <h5>{event.title.trim()}</h5>}
                    <p style={{ whiteSpace: 'pre-wrap' }}>{event.description}</p>
                </div>

                <Row className="mb-3">
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Building2 size={14} className="text-muted" />
                            <strong>Byggnad</strong>
                        </div>
                        {renderBuildingName()}
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Calendar size={14} className="text-muted" />
                            <strong>Datum</strong>
                        </div>
                        <span>
                            {event.inexactDate
                                ? event.date.getFullYear()
                                : formatDate(event.displayDate)}
                        </span>
                    </Col>
                </Row>

                {event.linkedComponents && event.linkedComponents.length > 0 && (
                    <div className="mb-3">
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Package size={14} className="text-muted" />
                            <strong>Komponenter</strong>
                        </div>
                        <div className="d-flex flex-column gap-1">
                            {event.linkedComponents.map(comp => (
                                <a
                                    key={comp.key}
                                    href={`/building/${event.buildingKey}/component/${comp.key}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {comp.displayName}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {event.attachments && Object.keys(event.attachments).length > 0 && (
                    <div className="mb-3">
                        <strong>Bilagor</strong>
                        <div className="mt-2">
                            <TimelineAttachments
                                attachments={event.attachments}
                                buildingKey={event.buildingKey}
                                showRemoveButton={false}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    };

    // Get the URL to open in new tab
    const getOpenUrl = (): string | null => {
        switch (event.eventType) {
            case TimelineEventType.INSPECTION_COMPLETED:
                return `/building/${event.buildingKey}/checks`;
            case TimelineEventType.COMPONENT_REPLACED:
                return event.componentKey
                    ? `/building/${event.buildingKey}/component/${event.componentKey}`
                    : null;
            case TimelineEventType.PROJECT_END:
                return event.projectId
                    ? `/building/${event.buildingKey}/projects/${event.projectId}`
                    : `/building/${event.buildingKey}/projects`;
            case TimelineEventType.MAINTENANCE_PLAN_ACTIVATED:
                // Archived plans have no viewer, so only the active plan links out
                return event.maintenancePlan?.isCurrent
                    ? `/maintenanceActive/${event.buildingKey}`
                    : null;
            default:
                return null;
        }
    };

    const openUrl = getOpenUrl();

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    {getIcon()}
                    {getEventTypeLabel(event.eventType)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {renderContent()}
            </Modal.Body>
            <Modal.Footer>
                {event.eventType === TimelineEventType.CUSTOM && onEdit && (
                    <Button
                        variant="outline-primary"
                        onClick={() => onEdit(event)}
                    >
                        <Pencil size={16} className="me-1" />
                        Redigera
                    </Button>
                )}
                {openUrl && (
                    <Button
                        variant="outline-primary"
                        href={openUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <ExternalLink size={16} className="me-1" />
                        Öppna i ny flik
                    </Button>
                )}
                <Button variant="secondary" onClick={onHide}>
                    Stäng
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TimelineEventDetailModal;
